import { useQuery } from "@tanstack/react-query";
import { useAgenticLogicRuleVersionDetail } from "@src/api/agentic-logic/get-agentic-logic-rule-version-detail";
import { agenticLogicKeys } from "@src/api/queryKeys";
import {
  AGENTIC_LOGIC_ENTRY_FILENAME,
  decodeAgenticLogicFiles,
} from "./agenticLogicMonacoTypes";

export const publishedRuleSourceKey = (
  ruleId: string,
  version: number | null
) =>
  [...agenticLogicKeys.versionDetail(ruleId, version ?? -1), "source"] as const;

/** Published artifacts are immutable; compare their content rather than save times. */
export function usePublishedRuleSource(ruleId: string, version: number | null) {
  const detail = useAgenticLogicRuleVersionDetail(ruleId, version);
  const url = detail.data?.detail.sourceUrl;
  const source = useQuery({
    queryKey: publishedRuleSourceKey(ruleId, version),
    enabled: version !== null && !!url,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const response = await fetch(url!, { signal });
      if (!response.ok) throw new Error("Published source could not be loaded");
      const files = decodeAgenticLogicFiles(await response.text());
      const value = files[AGENTIC_LOGIC_ENTRY_FILENAME];
      if (value === undefined)
        throw new Error("Published source is missing its entry file");
      return value;
    },
  });
  return {
    data: source.data,
    isLoading: version !== null && (detail.isLoading || source.isLoading),
    isError:
      detail.isError ||
      source.isError ||
      (version !== null && detail.isSuccess && !url),
  };
}
